import type { ModuleKey } from '@ctcells/core'
import { t, type MessageKey } from '@ctcells/i18n'

import type { TabItem } from './Tabs.tsx'

/**
 * 모니터링·패드 그림 탭 — 와이어프레임 `FIGTABS` 1660행.
 *
 * 🔴 **탭 구성이 모듈마다 다르다.** 셋을 같게 만들지 마라(§19-3 이 뭉개진 지점으로 지목한 곳).
 *    「전체」만 공통이고 나머지는 그 장비에 실제로 있는 부품이다 — 없는 부품을 그리면 거짓말이 된다.
 *
 * 값은 내부 키, 표기는 `figTab.*` 사전이 만든다.
 */
const FIG_TABS: Record<ModuleKey, readonly { id: string; labelKey: MessageKey }[]> = {
  cent: [
    { id: 'all', labelKey: 'figTab.all' },
    { id: 'trays', labelKey: 'figTab.trays' },
    { id: 'cent', labelKey: 'figTab.cent' },
    { id: 'vision', labelKey: 'figTab.vision' },
    // ⚠ 「3축 로봇」 탭은 여기 없다. **장비 관점 콘솔만** `gantry` 옵션으로 켠다(아래 `GANTRY_TAB`).
  ],
  asm: [
    { id: 'all', labelKey: 'figTab.all' },
    { id: 'trays', labelKey: 'figTab.trays' },
    { id: 'tc', labelKey: 'figTab.tc' },
    { id: 'mod', labelKey: 'figTab.asm' },
    { id: 'vision', labelKey: 'figTab.vision' },
  ],
  // 염색기 — 3축은 그리퍼가 아니라 피펫 캐리어고, L 트레이는 셰이커에 결착된 한 장뿐이다(§16-1·2)
  stain: [
    { id: 'all', labelKey: 'figTab.all' },
    { id: 'tray', labelKey: 'figTab.tray' },
    { id: 'pip', labelKey: 'figTab.pip' },
    { id: 'shaker', labelKey: 'figTab.shaker' },
    { id: 'vision', labelKey: 'figTab.vision' },
  ],
}

/**
 * 「3축 로봇」 탭 — **장비 관점 콘솔 전용**이라 기본 표에서 뺀 자리.
 *
 * 🔴 이 탭이 있는 장비는 **원심분리기 하나**다. 어셈블러·염색기에 만들어 붙이지 마라 —
 *    탭 목록이 곧 「전체」 보기에서 누를 수 있는 부품 목록이라(§19-3), 없는 탭을 만들면
 *    그림이 갈 곳 없는 자리로 이동한다.
 * ⚠ 6축 로봇(`robot`)과 다른 것이다. 저쪽은 모니터링 모듈 카드 줄에 있다.
 */
const GANTRY_TAB: Partial<Record<ModuleKey, { id: string; labelKey: MessageKey }>> = {
  cent: { id: 'gantry', labelKey: 'figTab.gantry' },
}

/**
 * 그 모듈의 그림 탭 목록.
 *
 * `maintenance` 권한이 있고 **패드일 때만** 「정비」 탭이 붙는다(`maintPanel()` 1802행).
 * 콘솔 모니터링에는 붙지 않는다 — 조작은 개발 › 제어에서만 한다(§1-3).
 *
 * `gantry` 는 **장비 관점 콘솔이 켜는 깃발**이다. 기본값 `false` 가 지금 콘솔·패드·태블릿이고,
 * 켜면 「비전」 뒤·「정비」 앞에 「3축 로봇」이 들어간다(v0.1.0 시점 순서 그대로).
 *
 * 🔴 **「비전」 탭은 «언제나» 선다 — eric 지시 2026-09-21 「비전 탭은 항상 있어야 해」.**
 *    전날(09-20) 「영상 자료가 없으면 탭을 안 붙인다」는 `vision` 옵션이 여기 있었고, 그래서
 *    live 화면에서 탭도 「전체」 보기의 카메라 아이콘도 사라졌다. **eric 이 그것을 뒤집었다.**
 *    ⇒ 비는 것은 **탭 «안»의 내용**이지 탭이 아니다. 빈 사유는 `FocusFigure` 가
 *    「카메라 영상 연결 없음」(`monitoring.noVisionFeed`)으로 말한다.
 *    **`vision` 옵션을 되살려 여기서 거르지 마라.** 탭이 없으면 `selectableComponents` 에서도
 *    빠져 아이콘이 갈 곳을 잃는다 — 그것이 eric 이 본 증상이다.
 */
export function figureTabs(
  moduleKey: ModuleKey,
  options: { maintenance?: boolean; gantry?: boolean } = {},
): TabItem[] {
  const spec = FIG_TABS[moduleKey]
  const gantry = options.gantry === true ? GANTRY_TAB[moduleKey] : undefined
  const withGantry = gantry === undefined ? spec : [...spec, gantry]
  const tabs = withGantry.map((tab) => ({ id: tab.id, label: t(tab.labelKey) }))
  return options.maintenance ? [...tabs, { id: 'maint', label: t('figTab.maint') }] : tabs
}
